/**
 * @param {object[]} players - Player rows for the match.
 * @param {string} teamPid - Team provider ID to filter by, if any.
 * @returns {object[]} Players filtered to the given team, or all players.
 */
export const filterPlayersByTeam = (players, teamPid) =>
    teamPid ? players.filter((item) => item.teamId === teamPid) : players;

/**
 * @param {object[]} playerAverages - Season-average rows for the match.
 * @param {object[]} players - Player rows to match averages against.
 * @returns {object[]|undefined} Averages for players present in `players`.
 */
export const filterAveragesByPlayers = (playerAverages, players) =>
    playerAverages?.filter((averageItem) =>
        players.find(
            (playerItem) =>
                playerItem.player.player.player.playerId ===
                averageItem.player.playerId
        )
    );

/**
 * @param {object[]} playerAverages - Season-average rows for the match.
 * @param {string} teamPid - Team provider ID to filter by, if any.
 * @returns {object[]} Averages filtered to the given team, or all averages.
 */
export const filterAveragesByTeam = (playerAverages, teamPid) =>
    teamPid
        ? playerAverages.filter((item) => item.team.teamId === teamPid)
        : playerAverages;

/**
 * Resolves row data when match player data is available.
 *
 * @param {object} args - Args.
 * @param {object[]} args.playersData - Player rows, already filtered by team.
 * @param {object} args.matchData - Combined match player/average data.
 * @param {string} args.dataType - Current data type ('totals'|'averages').
 * @param {boolean} args.noPlayerData - True if only averages data is used.
 * @param {Function} args.updateDataType - Updates the selected data type.
 * @param {Function} args.setRowData - Sets the table row data.
 * @param {Function} args.setNoDataAvailable - Sets the no-data-available flag.
 */
export const resolveRowsFromPlayers = ({
    playersData,
    matchData,
    dataType,
    noPlayerData,
    updateDataType,
    setRowData,
    setNoDataAvailable
}) => {
    if (dataType === 'totals') {
        if (!noPlayerData) {
            updateDataType('totals');

            /**
             * remapping jumper number from the player averages if it's missing in the playerData
             */
            const playersDataRemapped = playersData.map((player) => {
                return {
                    player: {
                        ...player.player,
                        jumperNumber:
                            player.player.jumperNumber !== 0
                                ? player.player.jumperNumber
                                : matchData.playerAverages.find(
                                      (playerAvg) =>
                                          playerAvg.player.playerId ===
                                          player.player.player.player.playerId
                                  )?.player.jumper ?? 0
                    },
                    playerStats: player.playerStats,
                    teamId: player.teamId
                };
            });

            setRowData(playersDataRemapped);
        }
        return;
    }

    const filteredAverages = filterAveragesByPlayers(
        matchData.playerAverages,
        playersData
    );

    if (filteredAverages?.length) {
        setRowData(filteredAverages);
    } else {
        setNoDataAvailable(true);
    }
};

/**
 * Resolves row data when there is no player data yet (upcoming matches),
 * falling back to season averages.
 *
 * @param {object} args - Args.
 * @param {object} args.matchData - Combined match player/average data.
 * @param {string} args.teamPid - Team provider ID to filter by, if any.
 * @param {string} args.dataType - Current data type ('totals'|'averages').
 * @param {boolean} args.noPlayerData - True if only averages data is used.
 * @param {Function} args.updateDataType - Updates the selected data type.
 * @param {Function} args.setNoPlayerData - Sets the no-player-data flag.
 * @param {Function} args.setRowData - Sets the table row data.
 * @param {Function} args.setNoDataAvailable - Sets the no-data-available flag.
 */
export const resolveRowsFromAverages = ({
    matchData,
    teamPid,
    dataType,
    noPlayerData,
    updateDataType,
    setNoPlayerData,
    setRowData,
    setNoDataAvailable
}) => {
    if (!matchData.playerAverages?.length) {
        return;
    }

    if (dataType !== 'totals') {
        const filterAverages = filterAveragesByTeam(
            matchData.playerAverages,
            teamPid
        );
        setRowData(filterAverages);
        return;
    }

    if (noPlayerData) {
        setNoDataAvailable(true);
        return;
    }

    updateDataType('averages');
    setNoPlayerData(true);
};
